'use client';

import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import useAuthStore from '@/store/auth';
import { useMutationEditAccount } from '@/api/queries/customers';
import toast from 'react-hot-toast';
import { EditAccountForm } from '@/types/form';
import { Loader2 } from 'lucide-react';

const schema = Yup.object().shape({
  customer_name: Yup.string().required('Customer Name is required'),
  street_address: Yup.string().required('Street Address is required'),
  city: Yup.string().required('City is required'),
  phone: Yup.string().min(8, 'Phone must be at least 8 characters').required('Phone is required'),
});

export default function AccountTab() {
  const user = useAuthStore((state) => state.user);
  const setLogin = useAuthStore((state) => state.setLogin);
  const { mutateAsync, isPending } = useMutationEditAccount();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditAccountForm>({
    // @ts-ignore
    resolver: yupResolver(schema),
    defaultValues: {
      customer_name: user?.customer_name || '',
      street_address: user?.street_address || '',
      city: user?.city || '',
      phone: user?.phone || '',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        customer_name: user.customer_name || '',
        street_address: user.street_address || '',
        city: user.city || '',
        phone: user.phone || '',
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: EditAccountForm) => {
    try {
      const response = await mutateAsync({ params: data });
      if (response) {
        setLogin(response);
      }
      toast.success('Account updated successfully!');
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Could not save profile';
      toast.error(`Update failed: ${message}`);
    }
  };

  const onValidationError = (formErrors: any) => {
    const errorKeys = Object.keys(formErrors);
    if (errorKeys.length > 0) {
      const firstError = formErrors[errorKeys[0]];
      const msg = typeof firstError?.message === 'string' ? firstError.message : 'Please check required fields';
      toast.error(msg);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onValidationError)} className="p-5 md:p-0 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
            Customer Name <span className="text-red-500">*</span>
          </label>
          <input
            {...register('customer_name')}
            type="text"
            placeholder="Enter customer name"
            className="w-full px-4 py-3 rounded-xl text-sm border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          />
          {errors.customer_name && (
            <p className="text-xs text-red-500 mt-1">{errors.customer_name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            {...register('phone')}
            type="tel"
            placeholder="Enter phone number"
            className="w-full px-4 py-3 rounded-xl text-sm border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          />
          {errors.phone && (
            <p className="text-xs text-red-500 mt-1">{errors.phone.message}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
            Street Address <span className="text-red-500">*</span>
          </label>
          <input
            {...register('street_address')}
            type="text"
            placeholder="Enter street address"
            className="w-full px-4 py-3 rounded-xl text-sm border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          />
          {errors.street_address && (
            <p className="text-xs text-red-500 mt-1">{errors.street_address.message}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 mb-1.5">
            City <span className="text-red-500">*</span>
          </label>
          <input
            {...register('city')}
            type="text"
            placeholder="Enter city"
            className="w-full px-4 py-3 rounded-xl text-sm border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-slate-800 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          />
          {errors.city && (
            <p className="text-xs text-red-500 mt-1">{errors.city.message}</p>
          )}
        </div>
      </div>

      <div className="pt-3 pb-28 md:pb-2 flex justify-end">
        <button
          type="submit"
          disabled={isPending}
          className="w-full md:w-auto md:min-w-[160px] py-3 px-6 rounded-xl font-semibold text-sm text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.99] disabled:opacity-60 transition-all flex items-center justify-center gap-2 shadow-sm"
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Account'
          )}
        </button>
      </div>
    </form>
  );
}
